import React, { useEffect, useRef, useState } from 'react';
import {
  Drawer,
  List,
  Typography,
  Spin,
  Empty,
  Badge,
  Avatar,
  message,
  Modal
} from 'antd';
import {
  DeleteOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import _axios from '../../services/api';
import { API_ROUTES } from '../../utils/constants';
import { getCurrentUser } from '../../utils/authHelpers';
import styles from './NotificationsDrawer.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { get_notifications } from '../../features/Organization/OrganizationInfo/OrganizationSlice';
import { get_Jobseeker_notifications } from '../../features/UserInfo/UserSlice';
import { IoCheckmarkDoneSharp } from "react-icons/io5";

const { Text, Paragraph } = Typography;

const NotificationsDrawer = ({ open, onClose }) => {
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const currentUser = getCurrentUser();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { notifications, notificationLoad } = useSelector((state) => state.global);
  const hasFetchedOnce = useRef(false);

  const API_URL =
    currentUser === "organization"
      ? API_ROUTES.ORGANIZATION.NOTIFICATIONS
      : API_ROUTES.JOBSEEKER.NOTIFICATIONS;

  const getData = () =>
    currentUser === "organization"
      ? dispatch(get_notifications({ navigate }))
      : dispatch(get_Jobseeker_notifications({ navigate }));

  const handleView = async (notification) => {
    setSelectedNotification(notification);
    setModalVisible(true);
  };

  const markAsViewed = async (id) => {
    try {
      await _axios.patch(`${API_URL}/${id}`, { viewed: true });
      getData();
    } catch (error) {
      console.error('Error updating notification', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await _axios.delete(`${API_URL}/${id}`);
      message.success('Notification removed');
      getData();
    } catch (error) {
      console.error('Error deleting notification', error);
    }
  };

  useEffect(() => {
    if (hasFetchedOnce.current) return;
    hasFetchedOnce.current = true;
    getData();
  }, [open]);

  const getTimeAgo = (date) => {
    const diff = (new Date() - new Date(date)) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} days ago`;
    return new Date(date).toLocaleDateString();
  };

  return (
    <>
      <Drawer
        title={<Badge count={notifications?.length}>Notifications</Badge>}
        placement="right"
        onClose={onClose}
        open={open}
        width={400}
        className={styles.drawrerNotification}
      >
        {notificationLoad ? (
          <Spin />
        ) : notifications?.length === 0 ? (
          <Empty description="No unread notifications" />
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={
              (Array.isArray(notifications) ? [...notifications] : []).sort(
                (a, b) => a?.viewed - b?.viewed
              )
            }
            renderItem={(item) => (
              <List.Item
                key={item._id}
                onClick={() => handleView(item)}
                className={`add-cursor ${item?.viewed ? styles.viewlist : styles.notificationItem}`}
                actions={[
                  <Avatar size={'small'} onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(item._id);
                  }} className='error-bg' icon={<DeleteOutlined
                    key="dismiss"
                   
                  />} />
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar style={{ backgroundColor: '#7265e6' }}>
                      {item.title?.charAt(0)?.toUpperCase() || 'N'}
                    </Avatar>
                  }
                  title={<Text strong>{item.title}</Text>}
                  description={
                    <div>
                      <Paragraph
                        ellipsis={{ rows: 1, expandable: false }}
                        style={{ marginBottom: 4 }}
                      >
                        {item.body}
                      </Paragraph>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        <IoCheckmarkDoneSharp size={15} className={item?.viewed ? 'blue-text' : 'text-black'} /> {getTimeAgo(item.sentAt)}
                      </Text>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Drawer>

      <Modal
        title={selectedNotification?.title}
        open={modalVisible}
        onCancel={async () => {
          setModalVisible(false);
          if (selectedNotification) await markAsViewed(selectedNotification._id);
          setSelectedNotification(null);
        }}
        onOk={async () => {
          setModalVisible(false);
          if (selectedNotification) await markAsViewed(selectedNotification._id);
          setSelectedNotification(null);
        }}
        okText="Close"
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <Text>{selectedNotification?.body}</Text>
        <br />
        <Text type="secondary" style={{ fontSize: 12 }}>
          {getTimeAgo(selectedNotification?.sentAt)}
        </Text>
      </Modal>
    </>
  );
};

export default NotificationsDrawer;
